import { useMemo } from 'react';

import { EWebtraderResetPasswordType } from '@aqx-core/common/api/config';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

export const useAuthRoutes = () => {
  const companyProfile = useCompanyProfile();
  // TODO: Handle registration URL for android/ios/web accordingly once doing 3.0 for mobile
  const {
    companyRegistrationUrl,
    crmForgotPasswordType,
    webtraderResetPasswordType,
    webtraderDemoRegistrationUrl,
    webtraderLiveRegistrationUrl
  } = companyProfile || {};

  const {
    hasCompanyLoginConfig,
    hasDemoAccountCreation,
    hasMT4DemoLoginConfig,
    hasMT5DemoLoginConfig,
    hasCentroidDemoLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT5LiveLoginConfig,
    hasCentroidLiveLoginConfig,
    hasResetPasswordAccess,
    hasForgotPasswordAccess,
    hasExperienceAccess
  } = useMemo(() => {
    return validateCompanyAccess(companyProfile);
  }, [companyProfile]);

  // ** LIVE **
  // There is currently no live sign up route, there is only redirection to the url specified in root admin
  const hasLiveLoginConfig = useMemo(() => {
    return hasMT4LiveLoginConfig || hasMT5LiveLoginConfig || hasCentroidLiveLoginConfig;
  }, [hasMT4LiveLoginConfig, hasMT5LiveLoginConfig, hasCentroidLiveLoginConfig]);

  const hasLiveSignUpButton = useMemo(() => {
    return hasLiveLoginConfig && !!webtraderLiveRegistrationUrl;
  }, [hasLiveLoginConfig, webtraderLiveRegistrationUrl]);

  const hasLiveForgotPasswordButton = useMemo(() => {
    return hasLiveLoginConfig && hasForgotPasswordAccess;
  }, [hasForgotPasswordAccess, hasLiveLoginConfig]);

  const hasLiveForgotPasswordRoute = useMemo(() => {
    return hasLiveForgotPasswordButton && webtraderResetPasswordType === EWebtraderResetPasswordType.INTEGRATION;
  }, [hasLiveForgotPasswordButton, webtraderResetPasswordType]);

  // ** DEMO **
  // For demo, there is only sign up for MT4, MT5 demo or Centroid demo.
  // There is no sign up UI/redirection for experience demo.
  // Experience demo sign up is handled from AQX website
  const hasDemoLoginConfig = useMemo(() => {
    return hasMT4DemoLoginConfig || hasMT5DemoLoginConfig || hasCentroidDemoLoginConfig;
  }, [hasMT4DemoLoginConfig, hasMT5DemoLoginConfig, hasCentroidDemoLoginConfig]);

  const { hasButton: hasDemoSignUpButton, hasRoute: hasDemoSignUpRoute } = useMemo(() => {
    if (!hasDemoAccountCreation || hasExperienceAccess) {
      return { hasButton: false, hasRoute: false };
    }

    // If sign up url not specified, use Sign up UI route instead
    if (hasDemoLoginConfig) {
      return { hasButton: true, hasRoute: !webtraderDemoRegistrationUrl };
    }
    return { hasButton: false, hasRoute: false };
  }, [hasDemoAccountCreation, hasExperienceAccess, hasDemoLoginConfig, webtraderDemoRegistrationUrl]);

  // For demo, only experience demo has reset password
  // If have demo reset password, have both route and button for it
  const hasDemoResetPassword = useMemo(() => {
    return hasDemoLoginConfig && hasExperienceAccess;
  }, [hasExperienceAccess, hasDemoLoginConfig]);

  // ** CRM **
  const hasCrmSignUpButton = useMemo(() => {
    return !!companyRegistrationUrl && hasCompanyLoginConfig;
  }, [companyRegistrationUrl, hasCompanyLoginConfig]);

  const hasCrmSignUpRoute = useMemo(() => {
    return companyRegistrationUrl?.toLocaleLowerCase() === 'form' && hasCompanyLoginConfig;
  }, [companyRegistrationUrl, hasCompanyLoginConfig]);

  const hasCrmForgotPasswordButton = useMemo(
    () => crmForgotPasswordType && hasResetPasswordAccess && hasCompanyLoginConfig,
    [crmForgotPasswordType, hasCompanyLoginConfig, hasResetPasswordAccess]
  );

  const hasCrmForgotPasswordRoute = useMemo(
    () => hasCrmForgotPasswordButton && crmForgotPasswordType === EWebtraderResetPasswordType.INTEGRATION,
    [crmForgotPasswordType, hasCrmForgotPasswordButton]
  );

  return {
    hasLiveSignUpButton,
    hasLiveForgotPasswordButton,
    hasLiveForgotPasswordRoute,
    hasDemoSignUpButton,
    hasDemoSignUpRoute,
    hasDemoResetPassword,
    hasCrmSignUpButton,
    hasCrmSignUpRoute,
    hasCrmForgotPasswordButton,
    hasCrmForgotPasswordRoute,
    companyProfile
  };
};
